import { Client as StompClient } from "@stomp/stompjs";
import get from "lodash/get";
import { action, reaction, observable, decorate } from "mobx";
import config from "utils/config";
import { captureException, logBreadcrumb } from "utils/logger";

export default class WebSocketService {
  subscriptions = new Map();
  subscribed = new Map();
  client = undefined;
  clientInitialized = false;
  autorunDisposer = null;
  reconnectAttempts = 0;
  maxReconnectAttempts = 10;
  connectionWanted = false;
  authenticatedIdentity = null;
  retryExhausted = false;
  lastFailureReportAt = 0;
  connected = false;
  connecting = false;

  async inject({ authStore }) {
    this.authStore = authStore;
    this.newStompClient();
  }

  newStompClient() {
    this.cleanup();
    this.client = new StompClient({
      brokerURL: `${config.wsUrl}/websockets`,
      reconnectDelay: 4000,
      connectionTimeout: 15000,
      heartbeatIncoming: 15000,
      heartbeatOutgoing: 7000,
      debug: process.env.NODE_ENV === "development" ? console.debug : () => {},
    });
    this.initStompClient();
    this.setupAutorun();
  }

  setupAutorun() {
    if (this.autorunDisposer) this.autorunDisposer();
    // Track authentication only: changes to socket state must not reactivate a stopped client.
    this.autorunDisposer = reaction(
      () => this.authStore.isConnected && !get(this.authStore.currentUser, "isSuperAdmin", false)
        ? `${get(this.authStore.currentUser, "id")}:${get(this.authStore.currentUser, "client.id")}` : null,
      identity => {
        const previousIdentity = this.authenticatedIdentity;
        this.authenticatedIdentity = identity;
        if (identity) {
          if (!this.client || (previousIdentity && previousIdentity !== identity)) this.newStompClient();
          else this.connect();
        }
        else this.disconnect();
      },
      { fireImmediately: true }
    );
  }

  initStompClient() {
    if (this.clientInitialized) return;
    const client = this.client;
    // Callbacks from a retired client cannot alter the new client's state.
    const current = () => client === this.client && this.connectionWanted && !this.retryExhausted;
    client.onConnect = () => {
      if (!current()) return;
      this.reconnectAttempts = 0;
      this.updateConnectionStatus(true);
      this.updateConnectingStatus(false);
      this.resubscribeAll();
    };
    client.onDisconnect = () => {
      if (!current()) return;
      this.subscribed.clear();
      this.updateConnectionStatus(false);
    };
    client.onStompError = frame => {
      if (!current()) return;
      logBreadcrumb({ category: "websocket", level: "warning",
        message: "Broker rejected the connection", data: { message: frame.headers.message } });
      // This is actionable even if the connection later recovers.
      this.reportPersistentFailure(new Error(`STOMP Error: ${frame.headers.message}`));
    };
    client.onWebSocketError = () => {
      if (!current()) return;
      // Close drives reconnection; this callback must not capture the same outage twice.
      logBreadcrumb({ category: "websocket", level: "warning", message: "WebSocket connection error" });
    };
    client.onWebSocketClose = event => {
      if (!current()) return;
      if (event.code === 1008 && event.reason === "Account disabled or expired") {
        this.authStore.setCurrentUser();
        this.disconnect();
        return;
      }
      // A closed transport cannot send UNSUBSCRIBE frames.
      this.subscribed.clear();
      this.updateConnectionStatus(false);
      this.reconnectAttempts++;
      logBreadcrumb({ category: "websocket", level: "warning", message: "WebSocket closed",
        data: { code: event.code, attempt: this.reconnectAttempts } });
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        this.retryExhausted = true;
        client.reconnectDelay = 0;
        this.updateConnectingStatus(false);
        client.deactivate();
        this.reportPersistentFailure(new Error("WebSocket reconnection attempts exhausted"));
      } else {
        this.updateConnectingStatus(true);
      }
    };
    this.clientInitialized = true;
  }

  reportPersistentFailure(error) {
    const now = Date.now();
    if (this.lastFailureReportAt && now - this.lastFailureReportAt < 60000) return;
    this.lastFailureReportAt = now;
    captureException(error);
  }

  updateConnectionStatus = connected => { this.connected = connected; };

  updateConnectingStatus = connecting => { this.connecting = connecting; };

  connect() {
    if (!this.client || this.client.active || this.connected || this.connecting || this.retryExhausted) return;
    this.connectionWanted = true;
    this.updateConnectingStatus(true);
    // activate starts a handshake; only onConnect marks it complete.
    try {
      this.client.activate();
    } catch (error) {
      this.updateConnectingStatus(false);
      captureException(error);
    }
  }

  subscribe(topic, callback) {
    if (!topic || typeof callback !== "function") throw new Error("Topic and callback are required for subscription");
    this.subscriptions.set(topic, callback);
    this.subscribeToTopic(topic, callback);
    return topic;
  }

  unsubscribe(topic) {
    this.subscriptions.delete(topic);
    const subscription = this.subscribed.get(topic);
    this.subscribed.delete(topic);
    if (subscription && this.client && this.client.connected) subscription.unsubscribe();
  }

  subscribeToTopic(topic, callback) {
    if (!this.connectionWanted || !this.client || !this.client.connected) return;
    try {
      const existing = this.subscribed.get(topic);
      if (existing) existing.unsubscribe();
      this.subscribed.set(topic, this.client.subscribe(topic, callback));
    } catch (error) {
      if (this.client.connected) captureException(error);
      else logBreadcrumb({ category: "websocket", message: "Connection lost during subscription" });
    }
  }

  resubscribeAll() {
    for (const [topic, callback] of this.subscriptions) this.subscribeToTopic(topic, callback);
  }

  clearActiveSubscriptions() {
    if (this.client && this.client.connected) {
      for (const subscription of this.subscribed.values()) {
        try { subscription.unsubscribe(); }
        catch (error) { if (this.client.connected) captureException(error); }
      }
    }
    this.subscribed.clear();
  }

  async publish(message) {
    if (!this.connectionWanted || !this.client || !this.client.connected) {
      throw new Error("Cannot publish message: WebSocket not connected");
    }
    try {
      return this.client.publish(message);
    } catch (error) {
      if (this.client.connected) captureException(error);
      throw error;
    }
  }

  async disconnect() {
    const client = this.client;
    this.connectionWanted = false;
    this.updateConnectionStatus(false);
    this.updateConnectingStatus(false);
    this.clearActiveSubscriptions();
    // Detach before awaiting: a subsequent connection can use a fresh client safely.
    this.client = undefined;
    this.clientInitialized = false;
    this.reconnectAttempts = 0;
    this.retryExhausted = false;
    if (client) {
      try { await client.deactivate(); }
      catch (error) { captureException(error); }
    }
  }

  async processMessage(message) {
    let json = {};
    try {
      json = JSON.parse(message.body);
    } catch (e) {
      captureException(e);
      return;
    }
    return json;
  }

  /**
   * Check if param clientId matches currentUser client' id.
   * @param {number} clientId
   */
  checkClientMatch(clientId) {
    // console.debug(
    //   `checkClientMatch with clientId ${clientId}: ${clientId ===
    //     this.authStore.currentUser.client.id}`
    // );
    return clientId === get(this.authStore, ["currentUser", "client", "id"]);
  }

  /**
   * Check if param userId matches current user's id.
   * @param {number} userId
   */
  checkUserMatch(userId) {
    return userId === get(this.authStore, ["currentUser", "id"]);
  }

  /**
   * Get current connection status information
   */
  getConnectionInfo() {
    return {
      connected: this.connected,
      connecting: this.connecting,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      subscriptionsCount: this.subscriptions.size,
      activeSubscriptionsCount: this.subscribed.size,
    };
  }

  cleanup() {
    if (this.autorunDisposer) this.autorunDisposer();
    this.autorunDisposer = null;
    this.disconnect();
    this.subscriptions.clear();
  }

  async destroy() {
    if (this.autorunDisposer) this.autorunDisposer();
    this.autorunDisposer = null;
    await this.disconnect();
    this.subscriptions.clear();
  }
}

decorate(WebSocketService, {
  connected: observable,
  connecting: observable,
  updateConnectionStatus: action,
  updateConnectingStatus: action,
});
